import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, InputAdornment, IconButton, Box, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { SearchContainer } from './styles';

const SearchBar = ({
  searchTerm,
  searchResults,
  showResults,
  isGlobalSearch,
  selectedSubcategory,
  onSearch,
  onResultSelect,
  onToggleSearchMode,
  showNoResultsMessage = true,
  width,
  onClear,
  readonly = false,
  allowCustomSegment = false,
  onCustomSegmentCreate
}) => {
  // Reference to the Autocomplete component
  const autocompleteRef = useRef(null);
  
  // Track dropdown state manually
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Track if user has interacted with the search field
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Handle input change (convert from the event format to the one expected by the parent)
  const handleInputChange = (event, newValue, reason) => {
    if (reason === 'clear') {
      onSearch({ target: { value: '' } });
    } else if (event) {
      // Mark as interacted when user types
      setHasInteracted(true);
      onSearch({ target: { value: event.target.value } });
    }
  };
  
  // Handle clearing the input
  const handleClear = () => {
    onSearch({ target: { value: '' } });
    
    // Call the onClear callback if provided
    if (typeof onClear === 'function') {
      onClear();
    }
  };

  // Handle toggle search mode
  const handleToggleSearchMode = () => {
    onToggleSearchMode(!isGlobalSearch);
  };

  // Create indexedOptions with a stable unique id for each option
  const indexedOptions = useMemo(() => {
    const options = [];

    // Add custom segment option at the beginning if allowed and has min length
    if (allowCustomSegment && onCustomSegmentCreate && searchTerm && searchTerm.trim && searchTerm.trim().length >= 3) {
      options.push({
        isCustomSkillOption: true,
        name: searchTerm.trim(),
        uniqueId: 'custom-skill-option'
      });
    }

    // Add search results after the custom skill option
    const searchOptions = searchResults?.map((result, index) => ({
      ...result,
      uniqueId: `${result.subcategory || ''}-${result.name || ''}-${index}`
    })) || [];

    return [...options, ...searchOptions];
  }, [searchResults, allowCustomSegment, onCustomSegmentCreate, searchTerm]);
  
  // Show message based on search state
  // When allowCustomSkill is enabled and has min length, the custom skill option is shown in the dropdown
  // So we only show the "no results" message box when there are truly no options (including the custom skill)
  const hasMinLength = searchTerm && searchTerm.trim && searchTerm.trim().length >= 3;
  const showMessage = searchTerm && searchTerm.trim && searchTerm.trim().length > 0 && indexedOptions.length === 0;
  
  // Handle dropdown open/close based on searchTerm
  useEffect(() => {
    // Only show dropdown if user has interacted with the field AND there's search text
    if (hasInteracted && searchTerm && searchTerm.trim && searchTerm.trim().length > 0) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  }, [searchTerm, hasInteracted]);
  
  // Handle result selection
  const handleResultSelection = (event, value) => {
    // Validate the value before proceeding
    if (!value) {
      console.warn('SearchBar: handleResultSelection called with undefined/null value');
      return;
    }

    if (value && typeof value !== 'string') {
      // Forcefully close the dropdown
      setIsDropdownOpen(false);
      setHasInteracted(false); // Reset interaction state

      // Force blur on the input to ensure dropdown really closes
      if (autocompleteRef.current) {
        const input = autocompleteRef.current.querySelector('input');
        if (input) {
          input.blur();
        }
      }

      // Check if this is the custom segment option
      if (value.isCustomSkillOption) {
        if (typeof onCustomSegmentCreate === 'function') {
          onCustomSegmentCreate(value.name);
        }
        setTimeout(() => {
          setIsDropdownOpen(false);
        }, 100);
        return;
      }

      // Call the parent's onResultSelect handler with proper validation
      if (typeof onResultSelect === 'function') {
        // Make sure we have a valid node to pass
        const nodeToPass = value.node || value;
        if (nodeToPass) {
          onResultSelect(nodeToPass);
        } else {
          console.warn('SearchBar: Cannot call onResultSelect - node is undefined/null');
        }
      } else {
        console.warn('SearchBar: onResultSelect is not a function');
      }

      // Use a timeout to ensure the dropdown is really closed
      setTimeout(() => {
        setIsDropdownOpen(false);
      }, 100);
    }
  };
  
  return (
    <SearchContainer $width={width}>
      <div 
        style={{ position: 'relative', width: '100%' }}
        ref={autocompleteRef}
      >
        <Autocomplete
          disabled={readonly}
          freeSolo
          fullWidth
          options={indexedOptions}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.name || '';
          }}
          value={null}
          inputValue={searchTerm}
          onInputChange={handleInputChange}
          onChange={handleResultSelection}
          filterOptions={(options) => options}
          open={isDropdownOpen && (indexedOptions.length > 0 || (showMessage && showNoResultsMessage))}
          onOpen={() => {
            // Only open if there's a search term and user has interacted
            if (searchTerm && searchTerm.trim && searchTerm.trim().length > 0 && hasInteracted) {
              setIsDropdownOpen(true);
            }
          }}
          onClose={() => {
            // Set a short timeout to allow click events to process first
            setTimeout(() => {
              setIsDropdownOpen(false);
            }, 50);
          }}
          sx={{ width: '100%' }}
          ListboxProps={{
            sx: {
              padding: 0,
              '& .MuiAutocomplete-option': {
                padding: '8px 16px',
                borderBottom: '1px solid #eaeaea',
                '&:last-child': {
                  borderBottom: 'none'
                }
              },
            }
          }}
          renderOption={(props, option) => {
            // Render custom skill option differently
            if (option.isCustomSkillOption) {
              return (
                <li
                  {...props}
                  key={option.uniqueId}
                  onClick={(e) => {
                    props.onClick(e);
                    setIsDropdownOpen(false);
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="primary"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      Add "{option.name}" to the Technology Radar 
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        mt: 0.5,
                        lineHeight: 1.2,
                        opacity: 0.8
                      }}
                    >
                     Custom Technology — will be temporarily added to a new category until reviewed 
                    </Typography>
                  </Box>
                </li>
              );
            }

            // Regular search result option
            return (
              <li
                {...props}
                key={option.uniqueId}
                onClick={(e) => {
                  props.onClick(e);
                  // Force dropdown to close on option click
                  setIsDropdownOpen(false);
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      color: 'text.primary'
                    }}
                  >
                    {option.name}
                  </Typography>
                  {option.subcategory && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        mt: 0.5,
                        lineHeight: 1.2,
                        opacity: 0.8
                      }}
                    >
                      {option.subcategory}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Search ${selectedSubcategory ? 'in ' + selectedSubcategory.name : 'all technology categories'}...`}
              variant="outlined"
              size="small"
              // Add onFocus handler to track interaction but NOT open dropdown automatically
              onFocus={() => {
                // Don't open dropdown on initial focus
                // Just mark that user has focused the field
                if (searchTerm && searchTerm.trim && searchTerm.trim().length > 0) {
                  setHasInteracted(true);
                }
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                  <>
                    {searchTerm && searchTerm.length > 0 && (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small" 
                          onClick={handleClear}
                          aria-label="clear search"
                          disabled={readonly}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )}
                    <InputAdornment position="end">
                      <IconButton 
                        size="small" 
                        onClick={handleToggleSearchMode}
                        aria-label="toggle search mode"
                        color={isGlobalSearch ? "primary" : "default"}
                        disabled={readonly}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
                }
              }}
            />
          )}
        />
        
        {/* Custom No Options Message - Only show when dropdown is open and showNoResultsMessage is true */}
        {/* Note: When allowCustomSkill is enabled, the custom skill option is included in the dropdown */}
        {showMessage && isDropdownOpen && showNoResultsMessage && (
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              mt: 0,
              border: '1px solid #ddd',
              borderRadius: 1,
              bgcolor: 'background.paper',
              zIndex: 9999,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              p: 2
            }}
          >
            <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
              {hasMinLength
                ? "No results found"
                : "Type at least 3 characters to search"}
            </Typography>
          </Box>
        )}
      </div>
    </SearchContainer>
  );
};

export default SearchBar; 
